import './ChartLibrary.scss';
import { useNavigate } from 'react-router-dom';
import { DashboardChartMeta } from '@blisspointmedia/bpm-types/dist/dashboarding';
import { Page } from '@Components/Page';
import { PAGE_NAMES } from '@Components/Page/page-constants';
import { Spinner } from '@Components/Spinner/Spinner';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { useChartLibrary } from './hooks/useChartLibrary';
import { useRecordPermissions } from './hooks/useRecordPermissions';
import { ViewOnlyBadge } from './ViewOnlyBadge';

interface ChartLibraryProps {
  company: string;
}

// Browse every saved chart for the company. Cards show metadata only (title + type) so the grid
// stays light with many charts; opening a card renders the live chart in the single-chart view, as a
// modal over this library.
// Owns its own <Page> (mirrors DashboardsList) now that ChartsPage no longer wraps both this and
// SingleChartView in one shared title bar.
export const ChartLibrary = ({ company }: ChartLibraryProps) => {
  const navigate = useNavigate();
  const { data: charts, isLoading, isError } = useChartLibrary(company);

  return (
    <Page title={PAGE_NAMES.CHARTS} pageType={PAGE_NAMES.CHARTS}>
      <ChartLibraryBody
        charts={charts}
        isLoading={isLoading}
        isError={isError}
        // Opens over the library as a modal (see ChartsPage) rather than replacing it.
        onOpen={chartId =>
          navigate(`/${company}/charts/${chartId}`, { state: { fromChartLibrary: true } })
        }
      />
    </Page>
  );
};

interface ChartLibraryBodyProps {
  charts: DashboardChartMeta[] | undefined;
  isLoading: boolean;
  isError: boolean;
  onOpen: (chartId: string) => void;
}

const ChartLibraryBody = ({ charts, isLoading, isError, onOpen }: ChartLibraryBodyProps) => {
  if (isLoading) return <Spinner />;
  if (isError) return <div className='chart-library-message'>Couldn&apos;t load charts.</div>;
  if (!charts?.length) {
    return <div className='chart-library-message'>No charts yet.</div>;
  }

  return (
    <div className='chart-library' data-testid='chart-library'>
      {charts.map(chart => (
        <ChartLibraryCard key={chart.id} chart={chart} onOpen={() => onOpen(chart.id)} />
      ))}
    </div>
  );
};

interface ChartLibraryCardProps {
  chart: DashboardChartMeta;
  onOpen: () => void;
}

// The card works its own permission out. `DashboardChartMeta` already carries both role levels, so
// nothing extra is fetched and no prop is threaded down from the list.
const ChartLibraryCard = ({ chart, onOpen }: ChartLibraryCardProps) => {
  const { isResolved, canEdit } = useRecordPermissions(chart);

  return (
    <InteractiveDiv
      className='chart-library-card'
      data-testid='chart-library-card'
      onClick={onOpen}
    >
      <div className='chart-library-card-meta'>
        <span className='chart-library-card-type'>{chart.chartType}</span>
        {isResolved && !canEdit && <ViewOnlyBadge />}
      </div>
      <span className='chart-library-card-title'>{chart.title}</span>
    </InteractiveDiv>
  );
};
